import './instrument'

import * as Sentry from '@sentry/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import RootLayout from '@/layouts/RootLayout'

/**
 * Rendered when the application has already failed, and it mounts ABOVE `RootLayout` - so it is outside
 * `ThemeProvider` and outside `CssBaseline` too, which is the part that is easy to miss here. Whatever
 * `CssBaseline` writes onto `body` is unavailable precisely when this renders.
 *
 * The colours come from the CSS variables with the inherited value as the fallback, and both halves earn
 * their place. The variables are the theme's own, so this screen follows the member's mode like everything
 * else. The fallback covers the case where they are not there yet: the pre-paint stylesheet in
 * `index.html` sets `color` on `:root` and `color: inherit` on `body`, which is what keeps this div
 * legible in dark rather than leaving it as user-agent black on a dark canvas. `verify:color-mode` asserts
 * that stylesheet still paints what the roles resolve to, so the fallback is not a guess.
 *
 * The layout is here because an unstyled div puts the message in the top-left corner of a blank page,
 * which reads as a broken deploy rather than as a handled error.
 */
function FallbackComponent(): JSX.Element {
  return (
    <div
      role='alert'
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        textAlign: 'center',
        backgroundColor: 'var(--mui-palette-background-default, inherit)',
        color: 'var(--mui-palette-text-primary, inherit)',
        fontFamily: "'Lato', sans-serif",
      }}
    >
      Something went wrong on our side. Reloading the page usually fixes it.
    </div>
  )
}

const myFallback = <FallbackComponent />

/**
 * `showDialog` is deliberately absent. It opens Sentry's user-feedback form in a cross-origin iframe, so
 * nothing here can style it: it would arrive in Sentry's own light chrome on top of a dark screen, and
 * there is no mechanism to reach inside and change that. Asking somebody who cannot sign in to fill in a
 * feedback form is also the wrong moment for it.
 */
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={myFallback}>
      <RootLayout />
    </Sentry.ErrorBoundary>
  </StrictMode>
)
